import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

export type ThemeChoice = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

const STORAGE_KEY = 'cr:theme';

interface ThemeState {
  /** Что выбрал администратор, включая «как в системе». */
  choice: ThemeChoice;
  /** Что реально показывается сейчас. */
  resolved: ResolvedTheme;
  setChoice: (choice: ThemeChoice) => void;
}

const ThemeContext = createContext<ThemeState | null>(null);

function readChoice(): ThemeChoice {
  const saved = localStorage.getItem(STORAGE_KEY);
  return saved === 'light' || saved === 'dark' || saved === 'system' ? saved : 'system';
}

function systemTheme(): ResolvedTheme {
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

/**
 * Тема живёт в атрибуте `data-theme` на `<html>`, а не в медиазапросе:
 * так выбор администратора и системная настройка описываются одним механизмом,
 * и правила в CSS остаются однозначными. `color-scheme` заодно перекрашивает
 * системные элементы — полосы прокрутки и всплывающие календари.
 */
function apply(theme: ResolvedTheme): void {
  const root = document.documentElement;
  root.dataset['theme'] = theme;
  root.style.colorScheme = theme;
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [choice, setChoiceState] = useState<ThemeChoice>(() => readChoice());
  const [resolved, setResolved] = useState<ResolvedTheme>(() =>
    readChoice() === 'system' ? systemTheme() : (readChoice() as ResolvedTheme),
  );

  useEffect(() => apply(resolved), [resolved]);

  // Пока выбрана системная тема, следим за её переключением на лету
  useEffect(() => {
    if (choice !== 'system') return;

    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const sync = () => setResolved(media.matches ? 'dark' : 'light');

    sync();
    media.addEventListener('change', sync);
    return () => media.removeEventListener('change', sync);
  }, [choice]);

  const setChoice = useCallback((next: ThemeChoice) => {
    localStorage.setItem(STORAGE_KEY, next);
    setChoiceState(next);
    setResolved(next === 'system' ? systemTheme() : next);
  }, []);

  const value = useMemo<ThemeState>(
    () => ({ choice, resolved, setChoice }),
    [choice, resolved, setChoice],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeState {
  const context = useContext(ThemeContext);
  if (!context) throw new Error('useTheme используется вне ThemeProvider');

  return context;
}

const OPTIONS: Array<{ value: ThemeChoice; label: string; icon: string }> = [
  { value: 'light', label: 'Светлая', icon: '☀' },
  { value: 'dark', label: 'Тёмная', icon: '☾' },
  { value: 'system', label: 'Как в системе', icon: '◐' },
];

/** Переключатель из трёх сегментов — выбор виден целиком, без раскрытия списка. */
export function ThemeToggle() {
  const { choice, setChoice } = useTheme();

  return (
    <div
      role="radiogroup"
      aria-label="Тема оформления"
      className="flex rounded-lg border border-neutral-200 p-0.5 dark:border-neutral-800"
    >
      {OPTIONS.map((option) => (
        <button
          key={option.value}
          role="radio"
          aria-checked={choice === option.value}
          title={option.label}
          onClick={() => setChoice(option.value)}
          className={`rounded-md px-2 py-1 text-xs transition ${
            choice === option.value
              ? 'bg-neutral-100 text-neutral-900 dark:bg-neutral-800 dark:text-neutral-100'
              : 'text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300'
          }`}
        >
          {option.icon}
        </button>
      ))}
    </div>
  );
}
